import {
  Button,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import clsx from 'clsx';
import { motion } from 'motion/react';
import {
  CircleAlertIcon,
  FileAudioIcon,
  FileIcon,
  FileImageIcon,
  FileTextIcon,
  FileVideoIcon,
  XIcon,
  type LucideIcon,
} from 'lucide-react';
import { formatFileSize } from '@/shared/utils/format';
import type { FileListItemProps } from './FileListItem.types';
import { useTranslation } from 'react-i18next';

type FileIconKind = 'pdf' | 'markdown' | 'image' | 'video' | 'audio' | 'text' | 'file';

function resolveFileIconKind(fileType?: string): FileIconKind {
  if (fileType === 'application/pdf') return 'pdf';
  if (fileType === 'text/markdown') return 'markdown';
  if (fileType?.startsWith('image/')) return 'image';
  if (fileType?.startsWith('video/')) return 'video';
  if (fileType?.startsWith('audio/')) return 'audio';
  if (fileType?.startsWith('text/')) return 'text';
  return 'file';
}

const FILE_ICON_BY_KIND: Record<FileIconKind, LucideIcon> = {
  pdf: FileTextIcon,
  markdown: FileTextIcon,
  image: FileImageIcon,
  video: FileVideoIcon,
  audio: FileAudioIcon,
  text: FileTextIcon,
  file: FileIcon,
};

export const FileListItem = (props: FileListItemProps) => {
  const { t } = useTranslation('common');

  const status = props.status ?? 'pending';

  // `done` chips are removed from the queue by the parent; rendering nothing
  // keeps the contract explicit even if a stale `done` slips through.
  if (status === 'done') {
    return null;
  }

  const isUploading = status === 'uploading';
  const isError = status === 'error';
  const iconKind = resolveFileIconKind(props.fileType);
  const FileIconComponent = FILE_ICON_BY_KIND[iconKind];
  const delay = props.delay ?? 0;

  return (
    <motion.div
      layout
      className={clsx(
        'flex items-center justify-between rounded-lg border border-border-light px-4 py-3',
        isError ? 'border-destructive' : 'border-border',
      )}
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 0 }}
      transition={{
        duration: 0.2,
        delay,
        layout: { duration: 0.2, ease: 'easeOut' },
      }}
    >
      <div className="flex items-center gap-3">
        <div
          data-testid="file-icon"
          data-icon-kind={iconKind}
          className="flex size-8 items-center justify-center rounded-md bg-accent"
        >
          <FileIconComponent className="size-5 text-primary" />
        </div>
        <span className="text-sm text-foreground">{props.fileName}</span>
        {props.fileSize !== undefined && (
          <span className="text-xs text-muted-foreground">{formatFileSize(props.fileSize)}</span>
        )}
      </div>
      <div className="flex items-center gap-1">
        {isError && props.errorMessage != null && (
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="mini"
                  aria-label={t('components.fileListItem.uploadErrorDetails')}
                  className="text-destructive"
                >
                  <CircleAlertIcon />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{props.errorMessage}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="mini"
                disabled={isUploading}
                onClick={isUploading ? undefined : props.onRemove}
                aria-label={
                  isUploading
                    ? t('components.fileListItem.uploading')
                    : t('components.fileListItem.removeFile')
                }
              >
                {isUploading ? <Spinner role="progressbar" /> : <XIcon />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('components.fileListItem.removeFile')}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </motion.div>
  );
};
